import { useMemo, useState } from 'react';
import {
  CloudUpload,
  Eye,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Trash,
} from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDateTime } from '@/lib/format';
import type { CategoriaPlantilla, EstadoPlantilla, EventoPlantilla } from '@/types/domain';
import type { CampoTemplate, PlantillaFiltros, TemplateCatalog, WhatsappTemplate } from '@/types/templates';
import { TemplateDialog } from './TemplateDialog';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import { TemplateSendDialog } from './TemplateSendDialog';
import {
  CATEGORIA_LABELS,
  CATEGORIA_META_LABELS,
  ENCABEZADO_LABELS,
  EVENTO_ORDER,
  countLabel,
  describeEncabezado,
  describeIdentidad,
  describeParametros,
  describeUltimaSync,
  eventoLabel,
  filterTemplates,
  formatNumber,
  summarizeCatalog,
  summarizeSeed,
  summarizeSync,
  summarizeTemplates,
  templateStatusMeta,
} from './templates-format';
import { activoPayload, seedPayload } from './templates-payload';
import {
  useDeleteTemplate,
  useSeedTemplates,
  useSubmitTemplateToMeta,
  useSyncTemplate,
  useSyncTemplates,
  useTemplateCatalog,
  useTemplates,
  useUpdateTemplate,
} from './use-templates';

/**
 * Pantalla `/templates` (paso 8 del frontend) — las plantillas de WhatsApp del negocio activo.
 *
 * Qué es y qué NO es:
 *  - ES el módulo 8 del backend visto desde el dashboard: el catálogo de los 9 eventos del sistema (qué
 *    texto recomienda y qué campos puede usar cada uno), las plantillas registradas del negocio, el
 *    envío a revisión de Meta, la sincronización del estado real y la prueba con un envío de verdad;
 *  - NO crea los encabezados de IMAGEN en Meta (Meta exige subir el archivo con la Resumable Upload
 *    API): esas plantillas se crean en WhatsApp Manager con el mismo nombre e idioma y después se
 *    registran acá (el borrador local + `sincronizar`);
 *  - NO edita lo que ya vive en Meta: una plantilla que salió de `BORRADOR` solo se puede ajustar en el
 *    mapeo local y en `activo` (el backend responde 400 a lo demás).
 *
 * Filtros con el reparto explícito entre servidor y cliente: `estado`, `categoria` y `evento` viajan al
 * backend (son query params reales del listado), mientras el buscador se resuelve sobre lo ya cargado
 * (el endpoint no pagina: devuelve el array entero).
 *
 * Permisos: leer el catálogo, el listado y previsualizar lo puede hacer cualquier miembro; crear,
 * editar, borrar, sembrar las recomendadas, enviar a Meta, sincronizar y mandar una prueba son solo de
 * ADMIN (`canEdit` esconde los controles y el backend responde 403 igual si se invocan a mano).
 */

/** Filtro de estado: los ocho estados de Meta más «todos». */
type EstadoFiltro = 'todos' | EstadoPlantilla;

/** Filtro de categoría local (`todas` = sin filtro). */
type CategoriaFiltro = 'todas' | CategoriaPlantilla;

/** Filtro de evento (`todos` = sin filtro). */
type EventoFiltro = 'todos' | EventoPlantilla;

const ESTADO_FILTERS: { value: EstadoFiltro; label: string }[] = [
  { value: 'todos', label: 'Todos los estados' },
  { value: 'BORRADOR', label: 'Borrador (sin enviar a Meta)' },
  { value: 'PENDIENTE', label: 'Pendiente de revisión' },
  { value: 'APROBADA', label: 'Aprobada' },
  { value: 'RECHAZADA', label: 'Rechazada' },
  { value: 'PAUSADA', label: 'Pausada' },
  { value: 'DESHABILITADA', label: 'Deshabilitada' },
  { value: 'ARCHIVADA', label: 'Archivada' },
  { value: 'DELETADA', label: 'Borrada en Meta' },
];

const CATEGORIA_FILTERS: { value: CategoriaFiltro; label: string }[] = [
  { value: 'todas', label: 'Al cliente y al admin' },
  { value: 'CLIENTE', label: 'Solo al cliente' },
  { value: 'ADMIN', label: 'Solo al administrador' },
];

/** Aviso que deja una acción de la cabecera (sembrar, sincronizar) o de una fila (enviar a Meta). */
interface AccionMensaje {
  tone: 'success' | 'warning';
  texto: string;
  detalles?: string[];
}

/**
 * Campos del catálogo del evento de una plantilla.
 *
 * La tabla los necesita para leer su mapeo con etiquetas («{{1}} → Nombre del cliente») sin conocer el
 * catálogo de memoria; si el catálogo todavía no llegó, el mapeo se pinta con las claves crudas.
 */
function camposDeEvento(catalogo: TemplateCatalog | null, evento: EventoPlantilla): CampoTemplate[] {
  return catalogo?.eventos.find((candidato) => candidato.evento === evento)?.campos ?? [];
}

export function TemplatesPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();

  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<EstadoFiltro>('todos');
  const [categoriaFilter, setCategoriaFilter] = useState<CategoriaFiltro>('todas');
  const [eventoFilter, setEventoFilter] = useState<EventoFiltro>('todos');

  /** Los filtros que viajan al backend (los tres son query params reales del listado). */
  const filtros: PlantillaFiltros = {
    estado: estadoFilter === 'todos' ? undefined : estadoFilter,
    categoria: categoriaFilter === 'todas' ? undefined : categoriaFilter,
    evento: eventoFilter === 'todos' ? undefined : eventoFilter,
  };

  const templatesQuery = useTemplates(tenantId, filtros);
  const catalogoQuery = useTemplateCatalog(tenantId);

  const updateTemplate = useUpdateTemplate(tenantId);
  const deleteTemplate = useDeleteTemplate(tenantId);
  const submitToMeta = useSubmitTemplateToMeta(tenantId);
  const syncOne = useSyncTemplate(tenantId);
  const syncAll = useSyncTemplates(tenantId);
  const seed = useSeedTemplates(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  /** Plantilla que se edita (`null` = alta). */
  const [editing, setEditing] = useState<WhatsappTemplate | null>(null);
  /** Evento con el que se abre el alta desde la tarjeta del catálogo. */
  const [eventoInicial, setEventoInicial] = useState<EventoPlantilla | null>(null);
  const [previewing, setPreviewing] = useState<WhatsappTemplate | null>(null);
  const [sending, setSending] = useState<WhatsappTemplate | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WhatsappTemplate | null>(null);
  /** ¿Borrar además la copia del WABA? (solo tiene sentido si la plantilla ya salió de BORRADOR). */
  const [eliminarEnMeta, setEliminarEnMeta] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Resultado de la última acción masiva o de una fila (se queda a la vista hasta que se cierre). */
  const [accionMensaje, setAccionMensaje] = useState<AccionMensaje | null>(null);

  const plantillas = templatesQuery.data ?? [];
  const catalogo = catalogoQuery.data ?? null;

  const visible = useMemo(() => filterTemplates(plantillas, search), [plantillas, search]);

  const filtering =
    search.trim().length > 0 ||
    estadoFilter !== 'todos' ||
    categoriaFilter !== 'todas' ||
    eventoFilter !== 'todos';
  /** Hay filas cargadas, pero nada pasa la búsqueda: se distingue de «todavía no hay nada». */
  const noMatches = plantillas.length > 0 && visible.length === 0;
  /** El backend no devolvió nada CON filtros: no es «no hay plantillas», es «nada cumple». */
  const emptyByFilter = templatesQuery.isSuccess && plantillas.length === 0 && filtering;
  /** Negocio sin ninguna plantilla (ni con filtros ni sin ellos). */
  const nothingYet = templatesQuery.isSuccess && plantillas.length === 0 && !filtering;
  /** Eventos del catálogo que todavía no tienen plantilla (lo que completa «sembrar recomendadas»). */
  const eventosSinPlantilla =
    catalogo?.eventos.filter((evento) => evento.plantilla === null).length ?? 0;

  /** Cualquier escritura del módulo bloquea los controles de la tabla. */
  const busy =
    updateTemplate.isPending ||
    deleteTemplate.isPending ||
    submitToMeta.isPending ||
    syncOne.isPending ||
    syncAll.isPending ||
    seed.isPending;

  function openCreate(evento: EventoPlantilla | null) {
    setEditing(null);
    setEventoInicial(evento);
    setActionError(null);
    setDialogOpen(true);
  }

  function openEdit(plantilla: WhatsappTemplate) {
    setEditing(plantilla);
    setEventoInicial(plantilla.evento);
    setActionError(null);
    setDialogOpen(true);
  }

  function openDelete(plantilla: WhatsappTemplate) {
    setPendingDelete(plantilla);
    setEliminarEnMeta(false);
    setActionError(null);
  }

  function clearFilters() {
    setSearch('');
    setEstadoFilter('todos');
    setCategoriaFilter('todas');
    setEventoFilter('todos');
  }

  /**
   * Activar/desactivar sin abrir el diálogo: un `PATCH` con un solo campo (`activo`).
   *
   * `activo` es del negocio, no de Meta: apagar una plantilla APROBADA la deja de usar el bot
   * (`enviable` pasa a false) sin perder el estado ni la aprobación.
   */
  async function toggleActivo(plantilla: WhatsappTemplate) {
    setActionError(null);
    try {
      await updateTemplate.mutateAsync({
        templateId: plantilla.id,
        payload: activoPayload(!plantilla.activo),
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  /**
   * `POST .../:id/enviar-meta`: crea la plantilla en el WABA para que Meta la revise.
   *
   * El caso frecuente de fallo es no tener WhatsApp conectado (400) o un encabezado de IMAGEN (400): en
   * los dos el mensaje del backend explica qué hacer, así que se muestra tal cual.
   */
  async function enviarAMeta(plantilla: WhatsappTemplate) {
    setActionError(null);
    setAccionMensaje(null);
    try {
      const actualizada = await submitToMeta.mutateAsync(plantilla.id);
      setAccionMensaje({
        tone: 'success',
        texto: `«${actualizada.nombreMeta}» quedó en Meta con estado ${
          templateStatusMeta(actualizada.estado).label
        }.`,
        detalles: ['Meta la revisa y avisa; usa «Sincronizar» para traer el estado real.'],
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  /** `POST .../:id/sincronizar`: trae el estado real de ESA plantilla del WABA. */
  async function sincronizar(plantilla: WhatsappTemplate) {
    setActionError(null);
    setAccionMensaje(null);
    try {
      const actualizada = await syncOne.mutateAsync(plantilla.id);
      setAccionMensaje({
        tone: actualizada.estado === 'APROBADA' ? 'success' : 'warning',
        texto: `«${actualizada.nombreMeta}» está ${templateStatusMeta(actualizada.estado).label} en Meta.`,
        detalles: actualizada.estadoDetalle ? [actualizada.estadoDetalle] : undefined,
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  /**
   * `POST .../recomendadas`: crea con los textos del catálogo un borrador por cada evento que falte.
   *
   * Se siembra SIN mandarlas a Meta (`enviarAMeta: false`) a propósito: el backend limita a 100 envíos
   * por hora y cada plantilla conviene revisarla antes, así que el envío se hace fila por fila con
   * «Enviar a Meta». Es idempotente (los eventos ya registrados vuelven en `existentes`).
   */
  async function runSeed() {
    setActionError(null);
    setAccionMensaje(null);
    try {
      const resultado = await seed.mutateAsync(seedPayload('', false));
      setAccionMensaje({
        tone: 'success',
        texto: summarizeSeed(resultado),
        detalles: resultado.errores.map(
          (error) => `${eventoLabel(error.evento)}: ${error.mensaje}`,
        ),
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  /** `POST .../sincronizar`: trae el estado de TODAS con una sola consulta a Meta. */
  async function runSyncAll() {
    setActionError(null);
    setAccionMensaje(null);
    try {
      const resultado = await syncAll.mutateAsync();
      setAccionMensaje({
        tone: resultado.noEncontradas.length > 0 ? 'warning' : 'success',
        texto: summarizeSync(resultado),
        detalles: resultado.noEncontradas.map(
          (nombre) => `Meta no tiene una plantilla «${nombre}» para esta fila local.`,
        ),
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    setActionError(null);
    try {
      const resultado = await deleteTemplate.mutateAsync({
        templateId: pendingDelete.id,
        eliminarEnMeta,
      });
      setPendingDelete(null);
      setEliminarEnMeta(false);
      setAccionMensaje({
        tone: 'success',
        texto: `Se borró «${pendingDelete.nombreMeta}» de este negocio${
          resultado.eliminadaEnMeta ? ' y del WABA de Meta' : ''
        }.`,
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Catálogo de eventos del sistema</CardTitle>
          <CardDescription>
            {catalogo ? `${summarizeCatalog(catalogo)}. ` : ''}
            Cada evento dispara una plantilla cuando el cliente no escribió en 24 h: acá está el texto que
            recomienda el sistema, los campos que puede usar cada uno y si tu negocio ya la tiene
            registrada.{' '}
            {canEdit
              ? `Los borradores que falten se crean de una sola vez con «Sembrar las recomendadas» (en la tarjeta de abajo)${
                  eventosSinPlantilla > 0 ? `: te faltan ${formatNumber(eventosSinPlantilla)}` : ''
                }.`
              : 'Tu rol en este negocio es de solo lectura: solo un administrador puede crear, enviar a Meta o sincronizar plantillas.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {catalogoQuery.isPending ? <PageLoader label="Cargando el catálogo de eventos…" /> : null}

          {catalogoQuery.isError ? <Alert>{getErrorMessage(catalogoQuery.error)}</Alert> : null}

          {catalogo ? (
            <ul className="divide-y divide-line">
              {catalogo.eventos.map((evento) => (
                <li
                  key={evento.evento}
                  className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 lg:flex-row lg:items-start lg:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body font-medium text-ink">{eventoLabel(evento.evento)}</p>
                      <Badge tone={evento.categoria === 'CLIENTE' ? 'brand' : 'info'}>
                        {CATEGORIA_LABELS[evento.categoria]}
                      </Badge>
                      {evento.plantilla ? (
                        <StatusBadge
                          label={templateStatusMeta(evento.plantilla.estado).label}
                          tone={templateStatusMeta(evento.plantilla.estado).tone}
                        />
                      ) : (
                        <Badge tone="neutral">Sin plantilla</Badge>
                      )}
                      {evento.encabezadoTipo === 'IMAGEN' ? (
                        <Badge tone="warning">Encabezado con imagen</Badge>
                      ) : null}
                    </div>

                    <p className="text-caption text-ink-muted">{evento.descripcion}</p>

                    <p className="text-micro text-ink-faint">
                      Nombre sugerido: {evento.nombreSugerido} · {ENCABEZADO_LABELS[evento.encabezadoTipo]}
                      {evento.pieSugerido ? ` · pie: «${evento.pieSugerido}»` : ''}
                    </p>

                    <p className="text-micro text-ink-faint">
                      Campos: {evento.campos.map((campo) => campo.clave).join(' · ')}
                    </p>

                    {evento.plantilla ? (
                      <p className="text-micro text-ink-muted">
                        Ya registrada como «{evento.plantilla.nombreMeta}» ({evento.plantilla.idioma}),{' '}
                        {evento.plantilla.enviable ? 'lista para usarse' : 'todavía no se usa'}.
                      </p>
                    ) : (
                      <p className="text-micro text-ink-muted">
                        Sin plantilla: el bot no puede abrir esta conversación fuera de la ventana de 24 h.
                      </p>
                    )}
                  </div>

                  <div className="shrink-0">
                    {canEdit && !evento.plantilla ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        disabled={busy}
                        onClick={() => openCreate(evento.evento)}
                      >
                        Crear el borrador
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<CloudUpload size={14} />}
                  loading={syncAll.isPending}
                  disabled={busy && !syncAll.isPending}
                  onClick={() => void runSyncAll()}
                >
                  Sincronizar con Meta
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Sparkles size={14} />}
                  loading={seed.isPending}
                  disabled={busy && !seed.isPending}
                  onClick={() => void runSeed()}
                >
                  Sembrar las recomendadas
                </Button>
                <Button size="sm" leftIcon={<Plus size={14} />} disabled={busy} onClick={() => openCreate(null)}>
                  Nueva plantilla
                </Button>
              </>
            ) : undefined
          }
        >
          <CardTitle>Plantillas del negocio</CardTitle>
          <CardDescription>
            {plantillas.length > 0 ? `${summarizeTemplates(plantillas)}. ` : ''}
            Una plantilla por evento e idioma: es la que el bot usa para escribir fuera de la ventana de
            24 h y la que Meta revisa. El texto y el nombre de las que ya están en Meta se editan en
            WhatsApp Manager; acá se ajustan el mapeo de las variables, el interruptor y el estado.{' '}
            {canEdit
              ? '«Enviar a Meta» las manda a revisión, «Sincronizar» trae su estado real y «Enviar prueba» manda un WhatsApp de verdad.'
              : 'Tu rol en este negocio es de solo lectura: puedes verlas y previsualizarlas, pero no editarlas.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {accionMensaje ? (
            <Alert tone={accionMensaje.tone}>
              <p className="font-medium">{accionMensaje.texto}</p>
              {accionMensaje.detalles && accionMensaje.detalles.length > 0 ? (
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {accionMensaje.detalles.map((detalle) => (
                    <li key={detalle}>{detalle}</li>
                  ))}
                </ul>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 -ml-2"
                onClick={() => setAccionMensaje(null)}
              >
                Entendido
              </Button>
            </Alert>
          ) : null}

          {actionError && !pendingDelete ? <Alert>{actionError}</Alert> : null}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <Input
              type="search"
              value={search}
              leftIcon={<Search size={16} />}
              placeholder="Buscar por nombre, evento, idioma o texto…"
              aria-label="Buscar plantillas"
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrar por estado en Meta"
              className="lg:w-60"
              value={estadoFilter}
              onChange={(event) => setEstadoFilter(event.target.value as EstadoFiltro)}
            >
              {ESTADO_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por categoría"
              className="lg:w-52"
              value={categoriaFilter}
              onChange={(event) => setCategoriaFilter(event.target.value as CategoriaFiltro)}
            >
              {CATEGORIA_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por evento"
              className="lg:w-64"
              value={eventoFilter}
              onChange={(event) => setEventoFilter(event.target.value as EventoFiltro)}
            >
              <option value="todos">Todos los eventos</option>
              {EVENTO_ORDER.map((evento) => (
                <option key={evento} value={evento}>
                  {eventoLabel(evento)}
                </option>
              ))}
            </Select>
          </div>

          {templatesQuery.isPending ? <PageLoader label="Cargando las plantillas…" /> : null}

          {templatesQuery.isError ? <Alert>{getErrorMessage(templatesQuery.error)}</Alert> : null}

          {nothingYet ? (
            <EmptyState
              icon={<Send size={22} />}
              title="Todavía no hay plantillas"
              description="Sin plantillas el bot no puede escribir fuera de la ventana de 24 h: los avisos de pedido confirmado, pago y envío quedarían sin salir. Empieza por «Sembrar las recomendadas»: crea los 9 borradores con los textos del catálogo, y después revisás cada uno y lo mandás a Meta."
              action={
                canEdit ? (
                  <span className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      leftIcon={<Sparkles size={16} />}
                      loading={seed.isPending}
                      onClick={() => void runSeed()}
                    >
                      Sembrar las recomendadas
                    </Button>
                    <Button leftIcon={<Plus size={16} />} onClick={() => openCreate(null)}>
                      Crear una a mano
                    </Button>
                  </span>
                ) : undefined
              }
            />
          ) : null}

          {emptyByFilter ? (
            <EmptyState
              icon={<Search size={22} />}
              title="No hay plantillas con esos filtros"
              description="Ninguna plantilla cumple el estado, la categoría o el evento que elegiste. Las de BORRADOR todavía no existen en Meta y las APROBADA son las que el bot puede usar hoy."
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  Quitar los filtros
                </Button>
              }
            />
          ) : null}

          {noMatches ? (
            <EmptyState
              icon={<Search size={22} />}
              title="Ninguna plantilla coincide"
              description={`Nada en las plantillas cargadas coincide con «${search.trim()}».`}
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  Quitar los filtros
                </Button>
              }
            />
          ) : null}

          {filtering && !noMatches ? (
            <p className="text-caption text-ink-muted">
              {countLabel(visible.length, 'plantilla visible', 'plantillas visibles')} de{' '}
              {formatNumber(plantillas.length)}.
            </p>
          ) : null}

          {visible.length > 0 ? (
            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Plantilla</TableHeaderCell>
                    <TableHeaderCell>Categoría</TableHeaderCell>
                    <TableHeaderCell>Estado en Meta</TableHeaderCell>
                    <TableHeaderCell>Mapeo del cuerpo</TableHeaderCell>
                    <TableHeaderCell>Se usa</TableHeaderCell>
                    <TableHeaderCell align="right">Acciones</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visible.map((plantilla) => (
                    <TableRow key={plantilla.id}>
                      <TableCell>
                        <span className="flex max-w-md flex-col gap-0.5">
                          <span className="text-body font-medium text-ink">{plantilla.nombreMeta}</span>

                          <span className="text-caption text-ink-muted">
                            {eventoLabel(plantilla.evento)} · {plantilla.idioma}
                          </span>

                          <span className="mt-0.5 text-micro text-ink-faint">
                            {describeUltimaSync(formatDateTime(plantilla.ultimaSyncAt))}
                          </span>

                          {plantilla.estadoDetalle ? (
                            <span className="text-micro text-danger-strong">
                              {plantilla.estadoDetalle}
                            </span>
                          ) : null}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className="flex flex-col items-start gap-1">
                          <Badge tone={plantilla.categoria === 'CLIENTE' ? 'brand' : 'info'}>
                            {CATEGORIA_LABELS[plantilla.categoria]}
                          </Badge>
                          <span className="text-micro text-ink-faint">
                            {CATEGORIA_META_LABELS[plantilla.categoriaMeta]}
                          </span>
                        </span>
                      </TableCell>

                      <TableCell>
                        <StatusBadge
                          label={templateStatusMeta(plantilla.estado).label}
                          tone={templateStatusMeta(plantilla.estado).tone}
                        />
                      </TableCell>

                      <TableCell className="max-w-sm">
                        <span className="flex flex-col gap-0.5">
                          <span className="line-clamp-2 text-caption text-ink-muted">
                            {describeParametros(
                              plantilla.parametros,
                              camposDeEvento(catalogo, plantilla.evento),
                            )}
                          </span>
                          <span className="text-micro text-ink-faint">
                            {describeEncabezado(plantilla)}
                          </span>
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className="flex items-center gap-2.5">
                          <Switch
                            checked={plantilla.activo}
                            label={`Usar la plantilla ${plantilla.nombreMeta} fuera de la ventana de 24 h`}
                            disabled={!canEdit || busy}
                            onChange={() => void toggleActivo(plantilla)}
                          />
                          <Badge tone={plantilla.enviable ? 'success' : 'neutral'}>
                            {plantilla.enviable ? 'Enviable' : 'No enviable'}
                          </Badge>
                        </span>
                      </TableCell>

                      <TableActionsCell>
                        <span className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Previsualizar ${plantilla.nombreMeta}`}
                            onClick={() => setPreviewing(plantilla)}
                          >
                            <Eye size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Enviar a Meta ${plantilla.nombreMeta}`}
                            disabled={
                              !canEdit ||
                              busy ||
                              plantilla.estado === 'APROBADA' ||
                              plantilla.estado === 'PENDIENTE'
                            }
                            onClick={() => void enviarAMeta(plantilla)}
                          >
                            <CloudUpload size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Sincronizar el estado de ${plantilla.nombreMeta}`}
                            disabled={!canEdit || busy}
                            onClick={() => void sincronizar(plantilla)}
                          >
                            <RefreshCw size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Enviar una prueba de ${plantilla.nombreMeta}`}
                            disabled={!canEdit}
                            onClick={() => setSending(plantilla)}
                          >
                            <Send size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${plantilla.nombreMeta}`}
                            disabled={!canEdit || busy}
                            onClick={() => openEdit(plantilla)}
                          >
                            <Pencil size={16} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Eliminar ${plantilla.nombreMeta}`}
                            disabled={!canEdit || busy}
                            onClick={() => openDelete(plantilla)}
                          >
                            <Trash size={16} />
                          </Button>
                        </span>
                      </TableActionsCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardTableArea>
          ) : null}


        </CardContent>
      </Card>

      <TemplateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tenantId={tenantId}
        catalogo={catalogo}
        template={editing}
        eventoInicial={eventoInicial}
      />

      <TemplatePreviewDialog
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        tenantId={tenantId}
        template={previewing}
        catalogo={catalogo}
      />

      <TemplateSendDialog
        open={sending !== null}
        onClose={() => setSending(null)}
        tenantId={tenantId}
        template={sending}
        catalogo={catalogo}
      />

      {/*
        Borrado con una decisión extra: `?eliminarEnMeta=true` borra ADEMÁS la copia del WABA. Ese
        interruptor no cabe en `ConfirmDialog` (que es un Modal con la decisión ya resuelta), así que se
        usa el `Modal` con el mismo lenguaje visual y el botón en rojo.
      */}
      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Eliminar la plantilla"
        description={
          pendingDelete
            ? `${describeIdentidad(pendingDelete)} · ${eventoLabel(pendingDelete.evento)}`
            : undefined
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setPendingDelete(null)}
              disabled={deleteTemplate.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={deleteTemplate.isPending}
              onClick={() => void confirmDelete()}
            >
              Eliminar la plantilla
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {actionError ? <Alert>{actionError}</Alert> : null}

          <p className="text-caption text-ink-muted">
            Se borra la fila de este negocio
            {pendingDelete?.estado === 'BORRADOR'
              ? '. Como nunca se envió a Meta, no hay nada que borrar en el WABA.'
              : `. Si el bot la estaba usando fuera de la ventana de 24 h, vuelve a intentar con el texto libre o con otra plantilla del evento.`}
          </p>

          {pendingDelete && pendingDelete.estado !== 'BORRADOR' ? (
            <div className="flex items-center justify-between gap-3 rounded-panel border border-line px-3.5 py-3">
              <div className="min-w-0">
                <p className="text-caption font-medium text-ink-soft">Borrar también en Meta</p>
                <p className="text-micro text-ink-faint">
                  Si lo dejas apagado, la plantilla sigue aprobada en el WABA y puedes volver a
                  registrarla sincronizando. Si lo prendes, se borra allá y no se puede deshacer.
                </p>
              </div>

              <span className="shrink-0">
                <Switch
                  checked={eliminarEnMeta}
                  label="Borrar también la plantilla en Meta"
                  disabled={deleteTemplate.isPending}
                  onChange={setEliminarEnMeta}
                />
              </span>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}


